import { useEffect, useState } from "react";
import {
	listProviderPortfolio,
	refreshProviderPortfolio,
	type PortfolioAccount,
} from "./connections-api.js";

function formatAdsCid(raw: string): string {
	const digits = raw.replace(/\D/g, "");
	if (digits.length !== 10) return raw;
	return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function adsAccountCid(account: PortfolioAccount): string {
	if (typeof account.meta.customerId === "string") {
		return account.meta.customerId;
	}
	const viaMcc = account.externalAccountId.match(
		/^customers\/\d+\/as\/customers\/(\d+)$/,
	);
	if (viaMcc?.[1]) return viaMcc[1];
	const direct = account.externalAccountId.match(/^customers\/(\d+)$/);
	return direct?.[1] ?? account.externalAccountId;
}

function adsManagerCid(account: PortfolioAccount): string | null {
	const viaMcc = account.externalAccountId.match(
		/^customers\/(\d+)\/as\/customers\/\d+$/,
	);
	return viaMcc?.[1] ?? null;
}

type PortfolioGroup = {
	key: string;
	label: string;
	badge: string | null;
	aside: string;
	rows: PortfolioAccount[];
};

function groupAdsPortfolio(accounts: PortfolioAccount[]): PortfolioGroup[] {
	const managers = accounts.filter((row) => row.meta.isManager === true);
	const clients = accounts.filter((row) => row.meta.isManager !== true);
	const byManager = new Map<string, PortfolioAccount[]>();
	const direct: PortfolioAccount[] = [];

	for (const client of clients) {
		const mcc = adsManagerCid(client);
		if (mcc) {
			const list = byManager.get(mcc) ?? [];
			list.push(client);
			byManager.set(mcc, list);
			continue;
		}
		direct.push(client);
	}

	const sortByName = (a: PortfolioAccount, b: PortfolioAccount) =>
		a.displayName.localeCompare(b.displayName);

	const groups: PortfolioGroup[] = [];
	for (const manager of managers.sort(sortByName)) {
		const cid = adsAccountCid(manager);
		const children = (byManager.get(cid) ?? []).sort(sortByName);
		byManager.delete(cid);
		groups.push({
			key: manager.id,
			label: manager.displayName.replace(/\s*\(manager\)\s*$/i, "").trim(),
			badge: "MCC",
			aside: formatAdsCid(cid),
			rows: children,
		});
	}

	for (const [mcc, children] of byManager) {
		groups.push({
			key: `mcc-${mcc}`,
			label: `Manager ${formatAdsCid(mcc)}`,
			badge: "MCC",
			aside: formatAdsCid(mcc),
			rows: children.sort(sortByName),
		});
	}

	if (direct.length > 0) {
		groups.push({
			key: "direct",
			label: "Direct access",
			badge: null,
			aside: String(direct.length),
			rows: direct.sort(sortByName),
		});
	}

	return groups;
}

/** Group by meta.accountName (GA4 accounts, GTM accounts, CF accounts, …). */
function groupByAccountName(accounts: PortfolioAccount[]): PortfolioGroup[] {
	const buckets = new Map<string, PortfolioAccount[]>();
	for (const account of accounts) {
		const name =
			typeof account.meta.accountName === "string" &&
			account.meta.accountName.trim().length > 0
				? account.meta.accountName.trim()
				: "Other";
		const list = buckets.get(name) ?? [];
		list.push(account);
		buckets.set(name, list);
	}

	const sortByName = (a: PortfolioAccount, b: PortfolioAccount) =>
		a.displayName.localeCompare(b.displayName);

	return [...buckets.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([label, rows]) => ({
			key: label,
			label,
			badge: null,
			aside: String(rows.length),
			rows: rows.sort(sortByName),
		}));
}

function pluralize(label: string, count: number): string {
	if (count === 1) return label;
	const irregular: Record<string, string> = {
		property: "properties",
		repository: "repositories",
		identity: "identities",
	};
	return irregular[label] ?? `${label}s`;
}

function rowSecondary(
	account: PortfolioAccount,
	variant: "ads" | "account" | "flat",
): string {
	if (variant === "ads") return formatAdsCid(adsAccountCid(account));
	if (variant === "account") {
		if (typeof account.meta.publicId === "string") {
			return account.meta.publicId;
		}
		// properties/516066199 → 516066199
		const prop = account.externalAccountId.match(/^properties\/(.+)$/);
		if (prop?.[1]) return prop[1];
		const zone = account.externalAccountId;
		return zone;
	}
	if (typeof account.meta.accountName === "string") {
		return account.meta.accountName;
	}
	if (typeof account.meta.publicId === "string") {
		return account.meta.publicId;
	}
	if (typeof account.meta.productionUrl === "string") {
		return account.meta.productionUrl;
	}
	if (typeof account.meta.url === "string") {
		return account.meta.url;
	}
	if (typeof account.meta.status === "string") {
		return String(account.meta.status);
	}
	return account.externalAccountId;
}

type Props = {
	/** Platform provider id (`ga4`, `cloudflare`, `google_ads`, …). */
	provider: string;
	title: string;
	resourceLabel: string;
	/** ads = MCC tree; account = group by meta.accountName; flat = single list. */
	variant?: "flat" | "ads" | "account";
	onChanged?: () => Promise<void>;
};

/**
 * Dense portfolio list. Ads groups by MCC; GA4/GTM/etc. by parent account.
 */
export function PortfolioPanel({
	provider,
	title,
	resourceLabel,
	variant = "flat",
	onChanged,
}: Props) {
	const [accounts, setAccounts] = useState<PortfolioAccount[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [refreshing, setRefreshing] = useState(false);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			const listed = await listProviderPortfolio(provider);
			if (cancelled) return;
			if (!listed.ok) {
				setError(listed.error);
				return;
			}
			setAccounts(listed.accounts);
		})();
		return () => {
			cancelled = true;
		};
	}, [provider]);

	const countLabel = (() => {
		if (!accounts) return "Loading…";
		if (variant === "ads") {
			const clients = accounts.filter((row) => row.meta.isManager !== true);
			return `${clients.length} client${clients.length === 1 ? "" : "s"} · agents can use all`;
		}
		return `${accounts.length} ${pluralize(resourceLabel, accounts.length)} · agents can use all`;
	})();

	const groups =
		accounts && variant === "ads"
			? groupAdsPortfolio(accounts)
			: accounts && variant === "account"
				? groupByAccountName(accounts)
				: null;

	return (
		<div className="bind-panel bind-panel--portfolio">
			<div className="bind-portfolio-head">
				<div>
					<p className="bind-panel-title">{title}</p>
					<p className="connection-sub">{countLabel}</p>
				</div>
				<button
					type="button"
					className="btn btn-quiet"
					disabled={refreshing || !accounts}
					onClick={() => {
						setRefreshing(true);
						void (async () => {
							const result = await refreshProviderPortfolio(provider);
							setRefreshing(false);
							if (!result.ok) {
								setError(result.error);
								return;
							}
							setError(null);
							setAccounts(result.accounts);
							await onChanged?.();
						})();
					}}
				>
					{refreshing ? "Refreshing…" : "Refresh"}
				</button>
			</div>
			{error && <p className="bind-panel-error">{error}</p>}
			{!accounts && !error && (
				<p className="connection-sub">Loading portfolio…</p>
			)}
			{accounts && accounts.length === 0 && (
				<p className="connection-sub">
					No accessible {pluralize(resourceLabel, 0)} yet. Try Refresh, or
					reconnect.
				</p>
			)}
			{accounts && accounts.length > 0 && variant === "flat" && (
				<ul className="bind-portfolio-rows bind-portfolio-rows--flat">
					{accounts.map((account) => (
						<li key={account.id} className="bind-portfolio-row">
							<span className="bind-portfolio-row-name">
								{account.displayName}
							</span>
							<span className="bind-portfolio-cid">
								{rowSecondary(account, "flat")}
							</span>
						</li>
					))}
				</ul>
			)}
			{groups && groups.length > 0 && (
				<div className="bind-portfolio-groups">
					{groups.map((group) => (
						<section key={group.key} className="bind-portfolio-group">
							<header className="bind-portfolio-group-head">
								<span className="bind-portfolio-group-label">
									{group.label}
									{group.badge ? (
										<span className="bind-portfolio-badge">{group.badge}</span>
									) : null}
								</span>
								<span className="bind-portfolio-cid">{group.aside}</span>
							</header>
							{group.rows.length === 0 ? (
								<p className="bind-portfolio-empty">
									No {pluralize(resourceLabel, 0)}
								</p>
							) : (
								<ul className="bind-portfolio-rows">
									{group.rows.map((account) => (
										<li key={account.id} className="bind-portfolio-row">
											<span className="bind-portfolio-row-name">
												{account.displayName}
											</span>
											<span className="bind-portfolio-cid">
												{rowSecondary(account, variant)}
											</span>
										</li>
									))}
								</ul>
							)}
						</section>
					))}
				</div>
			)}
		</div>
	);
}

/** Single-identity connectors (X, Gmail, Shopify, …) — one linked row. */
export function LinkedIdentityPanel({
	provider,
	title,
	onChanged,
}: {
	provider: string;
	title: string;
	onChanged?: () => Promise<void>;
}) {
	return (
		<PortfolioPanel
			provider={provider}
			title={title}
			resourceLabel="identity"
			variant="flat"
			onChanged={onChanged}
		/>
	);
}
